import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from 'react';
import { Theme } from '../themes/themeTokens';
import { defaultThemes } from '../themes/defaultThemes';
import {
  applyThemeVariables,
  saveActiveTheme,
  getActiveTheme,
  saveCustomThemes,
  getCustomThemes,
  getAllOriginalThemes,
  getOriginalTheme,
  duplicateTheme as duplicateThemeUtil,
  createEmptyTheme,
} from '../themes/themeUtils';
import { logger } from '../utils/logger';

const PREVIEW_THEME_KEY = 'ibx_preview_theme';

interface ThemeContextType {
  themes: Theme[];
  activeTheme: Theme;
  setActiveTheme: (themeId: string) => void;
  addTheme: (theme: Theme) => void;
  updateTheme: (themeId: string, updates: Partial<Theme>) => void;
  deleteTheme: (themeId: string) => void;
  duplicateTheme: (themeId: string) => void;
  createNewTheme: () => Theme;
  restoreDefaultTheme: () => void;
  reapplyActiveTheme: () => void;
  isPreviewMode: boolean;
  getThemeOriginal: (themeId: string) => Theme | null;
  restoreThemeToOriginal: (themeId: string) => Promise<void>;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [activeTheme, setActiveThemeState] = useState<Theme>(defaultThemes[0]);
  const [themes, setThemes] = useState<Theme[]>(defaultThemes);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  // Ref imutável para armazenar snapshots originais dos temas
  const themesOriginalRef = useRef<Map<string, Theme>>(new Map());

  // Aplica o tema padrão imediatamente no primeiro render
  useEffect(() => {
    applyThemeVariables(defaultThemes[0]);
  }, []);

  // Carrega temas e tema ativo do banco de forma assíncrona
  useEffect(() => {
    const initThemes = async () => {
      try {
        // Verifica se há um tema de preview temporário no sessionStorage
        const previewRaw = sessionStorage.getItem(PREVIEW_THEME_KEY);
        if (previewRaw) {
          try {
            const previewTheme: Theme = JSON.parse(previewRaw);
            const [customThemes, originalThemes] = await Promise.all([
              getCustomThemes(),
              getAllOriginalThemes(),
            ]);

            const allThemes = [...defaultThemes, ...customThemes];

            originalThemes.forEach(orig => {
              themesOriginalRef.current.set(orig.id, { ...orig });
            });

            setThemes(allThemes);
            setActiveThemeState(previewTheme);
            applyThemeVariables(previewTheme);
            setIsPreviewMode(true);
            setIsInitialized(true);

            logger.info('Modo preview inicializado', {
              previewThemeId: previewTheme.id,
              originalsLoaded: themesOriginalRef.current.size,
            });

            await logger.logThemeAudit({
              themeId: previewTheme.id,
              themeName: previewTheme.name,
              eventType: 'preview',
              eventDescription: 'Modo preview inicializado',
              userAction: 'Usuário abriu preview do tema em nova aba',
              severity: 'info',
            });

            return;
          } catch {
            sessionStorage.removeItem(PREVIEW_THEME_KEY);
          }
        }

        const [customThemes, savedThemeId, originalThemes] = await Promise.all([
          getCustomThemes(),
          getActiveTheme(),
          getAllOriginalThemes(),
        ]);

        // Temas padrão usam a si mesmos como original
        defaultThemes.forEach(theme => {
          themesOriginalRef.current.set(theme.id, { ...theme });
        });

        // Temas customizados usam o snapshot do banco
        originalThemes.forEach(orig => {
          themesOriginalRef.current.set(orig.id, { ...orig });
        });

        const allThemes = [...defaultThemes, ...customThemes];

        // ✅ CORREÇÃO BUG: Busca o tema salvo em TODOS os temas (padrão + customizados)
        // Antes só buscava em customThemes, por isso Corporativo não era encontrado
        const savedTheme = savedThemeId
          ? allThemes.find(t => t.id === savedThemeId)
          : null;

        const themeToActivate = savedTheme || defaultThemes[0];

        const updatedThemes = allThemes.map(t => ({
          ...t,
          active: t.id === themeToActivate.id,
        }));

        setThemes(updatedThemes);
        setActiveThemeState(themeToActivate);
        applyThemeVariables(themeToActivate);
        setIsInitialized(true);

        logger.info('Sistema de temas inicializado', {
          totalThemes: updatedThemes.length,
          customThemes: customThemes.length,
          originalsLoaded: themesOriginalRef.current.size,
          activeTheme: themeToActivate.id,
        });

        await logger.logThemeAudit({
          themeId: themeToActivate.id,
          themeName: themeToActivate.name,
          eventType: 'loaded',
          eventDescription: 'Sistema de temas inicializado com sucesso',
          userAction: 'Sistema carregou tema ativo',
          metadata: {
            totalThemes: updatedThemes.length,
            customThemes: customThemes.length,
            originalsLoaded: themesOriginalRef.current.size,
          },
          severity: 'info',
        });
      } catch (error) {
        logger.error('Erro ao inicializar temas:', error);

        await logger.logThemeAudit({
          themeName: 'Sistema',
          eventType: 'error',
          eventDescription: 'Erro ao inicializar sistema de temas',
          userAction: 'Sistema tentou carregar temas',
          metadata: { error: String(error) },
          severity: 'critical',
        });

        setThemes(defaultThemes);
        setActiveThemeState(defaultThemes[0]);
        applyThemeVariables(defaultThemes[0]);
        setIsInitialized(true);
      }
    };

    initThemes();
  }, []);

  // Notifica componentes sobre mudança de tema
  useEffect(() => {
    if (isInitialized) {
      const event = new CustomEvent('themeChanged', { detail: { theme: activeTheme } });
      window.dispatchEvent(event);
    }
  }, [activeTheme, isInitialized]);

  const reapplyActiveTheme = useCallback(() => {
    applyThemeVariables(activeTheme);
  }, [activeTheme]);

  const setActiveTheme = useCallback(async (themeId: string) => {
    setThemes(prev => {
      const theme = prev.find(t => t.id === themeId);
      if (!theme) return prev;

      const updatedThemes = prev.map(t => ({ ...t, active: t.id === themeId }));

      setActiveThemeState(theme);
      applyThemeVariables(theme);

      // ✅ CORREÇÃO BUG PRINCIPAL: Sempre salva o active_theme_id no banco,
      // independente de ser tema padrão (Corporativo, Premium, etc.) ou customizado.
      // Antes só salvava customThemes e não persistia o ID do tema ativo para temas padrão.
      saveActiveTheme(themeId);

      // Salva temas customizados separadamente (apenas os não-padrão)
      const customThemes = updatedThemes.filter(t => !t.isDefault);
      if (customThemes.length > 0) {
        saveCustomThemes(customThemes);
      }

      logger.info('Tema ativo alterado', { themeId, themeName: theme.name });

      logger.logThemeAudit({
        themeId: theme.id,
        themeName: theme.name,
        eventType: 'loaded',
        eventDescription: `Tema "${theme.name}" ativado no site`,
        userAction: 'Usuário aplicou tema',
        severity: 'info',
      });

      return updatedThemes;
    });
  }, []);

  const addTheme = useCallback(async (theme: Theme) => {
    const newTheme: Theme = {
      ...theme,
      active: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    themesOriginalRef.current.set(newTheme.id, { ...newTheme });

    setThemes(prev => {
      const updatedThemes = [...prev, newTheme];
      const customThemes = updatedThemes.filter(t => !t.isDefault);
      saveCustomThemes(customThemes);

      logger.info('Novo tema adicionado', {
        themeId: newTheme.id,
        themeName: newTheme.name,
        originalSnapshot: 'created',
      });

      logger.logThemeAudit({
        themeId: newTheme.id,
        themeName: newTheme.name,
        eventType: 'created',
        eventDescription: `Novo tema "${newTheme.name}" criado`,
        userAction: 'Usuário criou novo tema',
        snapshotAfter: newTheme as any,
        severity: 'info',
      });

      return updatedThemes;
    });
  }, []);

  const updateTheme = useCallback(async (themeId: string, updates: Partial<Theme>) => {
    setThemes(prev => {
      const oldTheme = prev.find(t => t.id === themeId);

      const updatedThemes = prev.map(t => {
        if (t.id !== themeId) return t;
        const updated: Theme = { ...t, ...updates, updatedAt: new Date().toISOString() };

        if (t.active) {
          setActiveThemeState(updated);
          applyThemeVariables(updated);
        }

        return updated;
      });

      const customThemes = updatedThemes.filter(t => !t.isDefault);
      saveCustomThemes(customThemes);

      logger.info('Tema atualizado', {
        themeId,
        updatedFields: Object.keys(updates),
        originalPreserved: themesOriginalRef.current.has(themeId),
      });

      const newTheme = updatedThemes.find(t => t.id === themeId);
      if (newTheme) {
        logger.logThemeAudit({
          themeId: newTheme.id,
          themeName: newTheme.name,
          eventType: 'modified',
          eventDescription: `Tema "${newTheme.name}" foi modificado`,
          userAction: 'Usuário editou tema',
          changesMade: updates as any,
          snapshotBefore: oldTheme as any,
          snapshotAfter: newTheme as any,
          severity: 'info',
        });
      }

      return updatedThemes;
    });
  }, []);

  const deleteTheme = useCallback(async (themeId: string) => {
    setThemes(prev => {
      const theme = prev.find(t => t.id === themeId);
      if (!theme || theme.isDefault) return prev;

      themesOriginalRef.current.delete(themeId);

      if (theme.active) {
        const fallback = defaultThemes[0];
        setActiveThemeState(fallback);
        applyThemeVariables(fallback);
        saveActiveTheme(fallback.id);
      }

      const updatedThemes = prev.filter(t => t.id !== themeId);
      const customThemes = updatedThemes.filter(t => !t.isDefault);
      saveCustomThemes(customThemes);

      logger.info('Tema deletado', { themeId, themeName: theme.name });

      logger.logThemeAudit({
        themeId: theme.id,
        themeName: theme.name,
        eventType: 'deleted',
        eventDescription: `Tema "${theme.name}" foi excluído`,
        userAction: 'Usuário deletou tema',
        snapshotBefore: theme as any,
        severity: 'warning',
      });

      return updatedThemes;
    });
  }, []);

  const duplicateTheme = useCallback(async (themeId: string) => {
    setThemes(prev => {
      const theme = prev.find(t => t.id === themeId);
      if (!theme) return prev;

      const duplicated = duplicateThemeUtil(theme);

      themesOriginalRef.current.set(duplicated.id, { ...duplicated });

      const updatedThemes = [...prev, duplicated];
      const customThemes = updatedThemes.filter(t => !t.isDefault);
      saveCustomThemes(customThemes);

      logger.info('Tema duplicado', {
        originalId: themeId,
        duplicatedId: duplicated.id,
        originalSnapshot: 'created',
      });

      logger.logThemeAudit({
        themeId: duplicated.id,
        themeName: duplicated.name,
        eventType: 'created',
        eventDescription: `Tema "${duplicated.name}" criado como cópia de "${theme.name}"`,
        userAction: 'Usuário duplicou tema',
        snapshotAfter: duplicated as any,
        metadata: { originalThemeId: themeId, originalThemeName: theme.name },
        severity: 'info',
      });

      return updatedThemes;
    });
  }, []);

  const createNewTheme = useCallback((): Theme => {
    const newTheme = createEmptyTheme();
    addTheme(newTheme);
    return newTheme;
  }, [addTheme]);

  const restoreDefaultTheme = useCallback(async () => {
    const restoredThemes = defaultThemes.map((t, i) => ({
      ...t,
      active: i === 0,
      isDefault: true,
    }));

    themesOriginalRef.current.clear();
    defaultThemes.forEach(theme => {
      themesOriginalRef.current.set(theme.id, { ...theme });
    });

    setThemes(restoredThemes);
    setActiveThemeState(defaultThemes[0]);
    applyThemeVariables(defaultThemes[0]);
    saveActiveTheme(defaultThemes[0].id);
    saveCustomThemes([]);

    logger.info('Temas restaurados para padrão', { themesCount: restoredThemes.length });

    await logger.logThemeAudit({
      themeName: 'Sistema',
      eventType: 'restored',
      eventDescription: 'Todos os temas foram restaurados para padrão do sistema',
      userAction: 'Usuário restaurou temas padrão',
      metadata: { themesCount: restoredThemes.length },
      severity: 'warning',
    });
  }, []);

  const getThemeOriginal = useCallback((themeId: string): Theme | null => {
    const original = themesOriginalRef.current.get(themeId);
    if (original) {
      logger.info('Snapshot original acessado', { themeId, themeName: original.name });
    }
    return original || null;
  }, []);

  const restoreThemeToOriginal = useCallback(async (themeId: string): Promise<void> => {
    const original = themesOriginalRef.current.get(themeId);

    if (!original) {
      logger.warn('Tentativa de restaurar tema sem snapshot original', { themeId });

      const originalFromDb = await getOriginalTheme(themeId);
      if (originalFromDb) {
        themesOriginalRef.current.set(themeId, { ...originalFromDb });
        updateTheme(themeId, originalFromDb);
        logger.info('Tema restaurado do banco', { themeId });

        await logger.logThemeAudit({
          themeId: originalFromDb.id,
          themeName: originalFromDb.name,
          eventType: 'restored',
          eventDescription: `Tema "${originalFromDb.name}" restaurado para estado original (do banco)`,
          userAction: 'Usuário restaurou tema para original',
          snapshotAfter: originalFromDb as any,
          severity: 'info',
        });

        return;
      }

      throw new Error('Snapshot original não encontrado');
    }

    updateTheme(themeId, {
      ...original,
      updatedAt: new Date().toISOString(),
    });

    logger.info('Tema restaurado para original', { themeId, themeName: original.name });

    await logger.logThemeAudit({
      themeId: original.id,
      themeName: original.name,
      eventType: 'restored',
      eventDescription: `Tema "${original.name}" restaurado para estado original`,
      userAction: 'Usuário restaurou tema para original',
      snapshotAfter: original as any,
      severity: 'info',
    });
  }, [updateTheme]);

  const value: ThemeContextType = {
    themes,
    activeTheme,
    setActiveTheme,
    addTheme,
    updateTheme,
    deleteTheme,
    duplicateTheme,
    createNewTheme,
    restoreDefaultTheme,
    reapplyActiveTheme,
    isPreviewMode,
    getThemeOriginal,
    restoreThemeToOriginal,
  };

  return (
    <ThemeContext.Provider value={value}>
      {isPreviewMode && (
        <div className="fixed top-0 left-0 right-0 z-[9999] bg-amber-500 text-white text-center py-2 px-4 text-sm font-semibold flex items-center justify-center gap-3 shadow-lg">
          <i className="ri-eye-line text-base"></i>
          <span>Modo de Pré-visualização — Este tema ainda não foi salvo</span>
          <button
            onClick={() => {
              sessionStorage.removeItem(PREVIEW_THEME_KEY);
              window.close();
            }}
            className="ml-4 px-3 py-1 bg-white/20 hover:bg-white/30 rounded-full text-xs font-bold transition-colors cursor-pointer whitespace-nowrap"
          >
            Fechar Pré-visualização
          </button>
        </div>
      )}
      <div className={isPreviewMode ? 'pt-10' : ''}>
        {children}
      </div>
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme deve ser usado dentro de um ThemeProvider');
  }
  return context;
};
